import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import useAsync from 'react-use/esm/useAsync';
import { makeStyles } from '@material-ui/core/styles';
import { useApi } from '@backstage/core-plugin-api';
import { searchApiRef, useSearch } from '@backstage/plugin-search-react';
import { SearchIcon } from '../../components/icons';
import { useRecentVisits } from '../home/useRecentVisits';
import { SearchPaletteFooter } from './SearchPaletteFooter';
import { SearchPaletteRow } from './SearchPaletteRow';
import { MAX_ROWS, useSearchPaletteGroups } from './useSearchPaletteGroups';
import type { PaletteSearchResult } from './types';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  queryRow: {
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    height: 54,
    padding: '0 18px',
    borderBottom: '1px solid var(--hone-portal-hairline-soft)',
  },
  queryIcon: {
    flex: 'none',
    width: 16,
    height: 16,
    opacity: 0.55,
    display: 'block',
  },
  input: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: 15,
    color: 'var(--bui-fg-primary)',
    '&::placeholder': { color: 'var(--hone-portal-fg-tertiary)' },
  },
  esc: {
    flex: 'none',
    fontFamily: 'var(--bui-font-monospace)',
    fontSize: 10,
    lineHeight: '16px',
    color: 'var(--bui-fg-secondary)',
    background: 'var(--hone-portal-fill-inset)',
    border: '1px solid var(--bui-border-1)',
    borderRadius: 4,
    padding: '0 6px',
  },
  list: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '8px 8px 4px',
  },
  eyebrow: {
    padding: '8px 12px 4px',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.03em',
    color: 'var(--hone-portal-fg-tertiary)',
  },
  message: {
    padding: '14px 12px 18px',
    fontSize: 13,
    color: 'var(--bui-fg-secondary)',
  },
});

/** Matches the vendor `SearchBar`, so the index sees the same request cadence. */
const DEBOUNCE_MS = 200;
const RECENT_LIMIT = 4;

export type HoneSearchPaletteProps = {
  /** Closes the dialog; the vendor `SearchModal` supplies it. */
  toggleModal: () => void;
};

/**
 * The ⌘K command palette.
 *
 * Mounted through `SearchModal`'s `children` render prop, so it replaces the
 * vendor body — query row, grouped rows, footer — while the vendor keeps the
 * `Dialog` shell (and its `aria-label`, which the theme's paper and overlay
 * rules key off) and the surrounding `SearchContextProvider`.
 *
 * `SearchResultPager` is deliberately gone. A palette is a shortlist; the rows
 * are capped in `useSearchPaletteGroups` and the full set lives on `/search`.
 */
export function HoneSearchPalette({ toggleModal }: HoneSearchPaletteProps) {
  const classes = useStyles();
  const navigate = useNavigate();
  const { term, setTerm, result } = useSearch();
  const searchApi = useApi(searchApiRef);
  const [query, setQuery] = useState(term);
  const [selected, setSelected] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { visits } = useRecentVisits(RECENT_LIMIT);

  /*
   * `autoFocus` alone is unreliable inside MUI's dialog: its focus trap moves
   * focus to the paper as the dialog opens, which can land after the input
   * mounts. Re-asserting it once on mount is what makes ⌘K type-ready.
   */
  useEffect(() => inputRef.current?.focus(), []);

  useEffect(() => {
    const timer = setTimeout(() => setTerm(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, setTerm]);

  /*
   * `useSearch` drops its value while a new query is in flight, so rendering
   * straight off it would blank the list on every keystroke. Holding the last
   * set keeps the rows in place until the next one lands.
   */
  const lastResults = useRef<PaletteSearchResult[]>([]);
  if (result.value) {
    lastResults.current = result.value.results;
  }

  /*
   * `useSearch`'s query is unfiltered across every collator, ranked by Lunr
   * relevance alone — a common word can bury the one matching catalog entity
   * under dozens of doc-page hits, so it never lands in that query's result
   * window at all. A second, catalog-only query guarantees catalog rows a
   * slot whenever any exist. `useSearchPaletteGroups`'s per-group cap is
   * dynamic (it divides `MAX_ROWS` across however many groups end up with
   * results), so this fetches up to the whole row budget — the group cap
   * that actually lands can never exceed it.
   */
  const lastCatalogResults = useRef<PaletteSearchResult[]>([]);
  const catalogResult = useAsync(async () => {
    if (!term) {
      return { results: [] };
    }
    return searchApi.query({
      term,
      types: ['software-catalog'],
      pageLimit: MAX_ROWS,
    });
  }, [term, searchApi]);
  if (catalogResult.value) {
    lastCatalogResults.current = catalogResult.value.results;
  }

  const results = useMemo(() => {
    // `Location` entities are catalog-registration wiring (the entries that
    // point the catalog at other catalog-info.yaml files), never something a
    // person is searching for.
    const isSearchable = ({ document }: PaletteSearchResult) =>
      document.kind?.toLowerCase() !== 'location';

    const primary = (result.value?.results ?? lastResults.current).filter(
      isSearchable,
    );
    const seen = new Set(primary.map(({ document }) => document.location));
    const catalogOnly = lastCatalogResults.current
      .filter(isSearchable)
      .filter(({ document }) => !seen.has(document.location));
    return [...primary, ...catalogOnly];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.value, catalogResult.value]);

  const groups = useSearchPaletteGroups(term, results, visits);

  // Flattened once so ↑/↓ walk rows and ⇥ can jump to the next group's first.
  const flat = useMemo(
    () =>
      groups.flatMap((group, groupIndex) =>
        group.items.map(item => ({ item, groupIndex })),
      ),
    [groups],
  );

  // Any change to the list invalidates the cursor; the top row is always live.
  useEffect(() => setSelected(0), [flat]);

  useEffect(() => {
    listRef.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const open = (index: number) => {
    const entry = flat[index];
    if (!entry) {
      return;
    }
    navigate(entry.item.to);
    toggleModal();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (flat.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected(current => (current + 1) % flat.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected(current => (current - 1 + flat.length) % flat.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      open(selected);
    } else if (event.key === 'Tab') {
      event.preventDefault();
      const here = flat[selected]?.groupIndex ?? 0;
      const next =
        (here + (event.shiftKey ? -1 : 1) + groups.length) % groups.length;
      const at = flat.findIndex(entry => entry.groupIndex === next);
      if (at !== -1) {
        setSelected(at);
      }
    }
  };

  const body = () => {
    if (flat.length > 0) {
      let cursor = -1;
      return groups.map(group => (
        <div key={group.id} role="group" aria-label={group.label}>
          <div className={classes.eyebrow}>{group.label}</div>
          {group.items.map(item => {
            cursor += 1;
            const index = cursor;
            return (
              <SearchPaletteRow
                key={item.id}
                item={item}
                term={term}
                isSelected={index === selected}
                onSelect={() => open(index)}
                onHover={() => setSelected(index)}
              />
            );
          })}
        </div>
      ));
    }
    if (result.loading) {
      return <p className={classes.message}>Searching…</p>;
    }
    return <p className={classes.message}>No results for “{term}”.</p>;
  };

  return (
    <div className={classes.root}>
      <div className={classes.queryRow}>
        <SearchIcon className={classes.queryIcon} />
        {/*
         * The input keeps focus for the palette's whole life and carries the
         * navigation keys itself, which is what makes typing and arrowing feel
         * like one control rather than two.
         */}
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          ref={inputRef}
          className={classes.input}
          type="text"
          role="combobox"
          aria-expanded
          aria-label="Search or jump to"
          aria-controls="hone-search-palette-results"
          aria-activedescendant={flat[selected]?.item.id}
          placeholder="Search or jump to…"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <span className={classes.esc}>esc</span>
      </div>
      <div
        id="hone-search-palette-results"
        ref={listRef}
        role="listbox"
        aria-label="Search results"
        className={classes.list}
      >
        {body()}
      </div>
      <SearchPaletteFooter />
    </div>
  );
}
